import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { adminApi, formatApiErrorDetail } from "../lib/authApi";

const ENTITY_CONFIG = {
  materials: {
    label: "Materials",
    fields: [
      { key: "code", label: "Code *", required: true },
      { key: "name", label: "Name *", required: true },
      { key: "category", label: "Category" },
      { key: "multiplier", label: "Cost Multiplier *", type: "number", required: true },
      { key: "density", label: "Density (g/cm³)", type: "number" },
      { key: "grades", label: "Grades (comma-separated)", type: "list" },
      { key: "notes", label: "Notes" },
    ],
    row: (i) => [i.code, i.name, i.category, `× ${i.multiplier}`, (i.grades || []).join(", ")],
  },
  processes: {
    label: "Processes",
    fields: [
      { key: "code", label: "Code *", required: true },
      { key: "name", label: "Name *", required: true },
      { key: "category", label: "Category" },
      { key: "rate", label: "Unit Rate (INR) *", type: "number", required: true },
      { key: "setup_charge", label: "Setup Charge (INR)", type: "number" },
      { key: "unit", label: "Unit" },
      { key: "notes", label: "Notes" },
    ],
    row: (i) => [i.code, i.name, `₹${i.rate}/${i.unit || "pcs"}`, `Setup ₹${i.setup_charge || 0}`, i.category],
  },
  machines: {
    label: "Machines",
    fields: [
      { key: "code", label: "Code *", required: true },
      { key: "name", label: "Name *", required: true },
      { key: "machine_type", label: "Type" },
      { key: "process_code", label: "Process Code" },
      { key: "capability", label: "Capability" },
      { key: "capacity", label: "Capacity" },
      { key: "notes", label: "Notes" },
    ],
    row: (i) => [i.code, i.name, i.machine_type, i.process_code, i.capacity],
  },
  rates: {
    label: "Rate Cards",
    fields: [
      { key: "entity_type", label: "Entity Type *", required: true },
      { key: "ref_name", label: "Reference Name *", required: true },
      { key: "rate", label: "Rate *", type: "number", required: true },
      { key: "setup_charge", label: "Setup Charge", type: "number" },
      { key: "unit", label: "Unit" },
      { key: "currency", label: "Currency" },
      { key: "notes", label: "Notes" },
    ],
    row: (i) => [i.entity_type, i.ref_name, `${i.currency || "INR"} ${i.rate}/${i.unit || "pcs"}`, `Setup ${i.setup_charge || 0}`],
  },
};

const toFormState = (cfg, item) => {
  const s = {};
  cfg.fields.forEach((f) => {
    const v = item?.[f.key];
    s[f.key] = f.type === "list" ? (v || []).join(", ") : v == null ? "" : String(v);
  });
  s.active = item ? !!item.active : true;
  return s;
};

const EntityForm = ({ entity, cfg, item, onDone, onCancel }) => {
  const [form, setForm] = useState(() => toFormState(cfg, item));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = { active: form.active };
    for (const f of cfg.fields) {
      const raw = (form[f.key] || "").trim();
      if (f.required && !raw) {
        toast.error(`${f.label.replace(" *", "")} is required.`);
        return;
      }
      if (f.type === "number") {
        if (raw === "") {
          if (f.key === "density") payload[f.key] = null;
          else payload[f.key] = 0;
        } else {
          const n = parseFloat(raw);
          if (Number.isNaN(n) || n < 0) {
            toast.error(`${f.label.replace(" *", "")} must be a valid number.`);
            return;
          }
          payload[f.key] = n;
        }
      } else if (f.type === "list") {
        payload[f.key] = raw ? raw.split(",").map((x) => x.trim()).filter(Boolean) : [];
      } else {
        payload[f.key] = raw;
      }
    }
    if (entity === "processes" && !payload.unit) payload.unit = "pcs";
    if (entity === "rates") {
      if (!payload.unit) payload.unit = "pcs";
      if (!payload.currency) payload.currency = "INR";
    }
    setSaving(true);
    try {
      const { data } = item
        ? await adminApi.patch(`/library/${entity}/${item.id}`, payload)
        : await adminApi.post(`/library/${entity}`, payload);
      toast.success(item ? "Updated." : "Added.");
      onDone(data, !!item);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-muted/40 border border-border space-y-4" data-testid={`library-form-${entity}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold font-heading text-sm">{item ? `Edit ${item.code || item.ref_name}` : `Add ${cfg.label.replace(/s$/, "")}`}</h4>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid={`library-form-close-${entity}`}>
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cfg.fields.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label className="text-xs">{f.label}</Label>
            <Input value={form[f.key]} type={f.type === "number" ? "number" : "text"}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`library-input-${entity}-${f.key}`} />
          </div>
        ))}
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="h-4 w-4 accent-primary" data-testid={`library-active-checkbox-${entity}`} />
            Active
          </label>
        </div>
      </div>
      <Button size="sm" disabled={saving} onClick={save} className="rounded-full px-6" data-testid={`library-save-btn-${entity}`}>
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : null}
        {item ? "Save Changes" : "Add"}
      </Button>
    </div>
  );
};

const EntityPanel = ({ entity, items, setItems }) => {
  const cfg = ENTITY_CONFIG[entity];
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const onDone = (data, wasEdit) => {
    setItems(wasEdit ? items.map((i) => (i.id === data.id ? data : i)) : [data, ...items]);
    setFormOpen(false);
    setEditing(null);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.code || item.ref_name}? This cannot be undone.`)) return;
    setDeleting(item.id);
    try {
      await adminApi.delete(`/library/${entity}/${item.id}`);
      setItems(items.filter((i) => i.id !== item.id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} {cfg.label.toLowerCase()}</p>
        {!formOpen && !editing && (
          <Button size="sm" onClick={() => setFormOpen(true)} className="rounded-full" data-testid={`library-add-btn-${entity}`}>
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add
          </Button>
        )}
      </div>
      {(formOpen || editing) && (
        <EntityForm entity={entity} cfg={cfg} item={editing} onDone={onDone}
          onCancel={() => { setFormOpen(false); setEditing(null); }} />
      )}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground py-6" data-testid={`library-empty-${entity}`}>Nothing here yet.</p>}
        {items.map((item) => (
          <div key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg bg-card border border-border text-sm" data-testid={`library-item-${entity}-${item.id}`}>
            {cfg.row(item).map((cell, i) => (
              i === 0
                ? <span key={i} className="font-mono-tech font-bold text-primary">{cell}</span>
                : cell ? <span key={i} className={i === 1 ? "font-medium" : "text-muted-foreground text-xs"}>{cell}</span> : null
            ))}
            {!item.active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
            <span className="ml-auto flex gap-2">
              <button onClick={() => { setEditing(item); setFormOpen(false); }} className="p-2 -m-1 text-muted-foreground hover:text-primary transition-colors duration-200" data-testid={`library-edit-btn-${entity}-${item.id}`}>
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(item)} disabled={deleting === item.id} className="p-2 -m-1 text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`library-delete-btn-${entity}-${item.id}`}>
                {deleting === item.id ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Trash2 className="h-4 w-4" strokeWidth={1.5} />}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminLibrarySection = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, p, mc, r] = await Promise.all(
          ["materials", "processes", "machines", "rates"].map((e) => adminApi.get(`/library/${e}`))
        );
        setData({ materials: m.data, processes: p.data, machines: mc.data, rates: r.data });
      } catch {
        toast.error("Failed to load library.");
        setData({ materials: [], processes: [], machines: [], rates: [] });
      }
    })();
  }, []);

  if (!data) {
    return <div className="py-12 flex justify-center" data-testid="library-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" strokeWidth={1.5} /></div>;
  }

  return (
    <Tabs defaultValue="materials" data-testid="admin-library-section">
      <TabsList className="w-full sm:w-auto justify-start overflow-x-auto scrollbar-none tab-strip-fade h-auto">
        {Object.entries(ENTITY_CONFIG).map(([key, cfg]) => (
          <TabsTrigger key={key} value={key} data-testid={`library-tab-${key}`}>{cfg.label} ({data[key].length})</TabsTrigger>
        ))}
      </TabsList>
      {Object.keys(ENTITY_CONFIG).map((key) => (
        <TabsContent key={key} value={key} className="mt-6">
          <EntityPanel entity={key} items={data[key]} setItems={(items) => setData({ ...data, [key]: items })} />
        </TabsContent>
      ))}
    </Tabs>
  );
};
